import { useEffect, useState } from 'react';
import type { VavClient, Contact, Conversation } from '@vav/client-core';
import type { ConnectionStatus } from '@vav/client-core';
import { NewChatDialog } from './NewChatDialog.tsx';
import { PhoneDialog } from './PhoneDialog.tsx';
import { InviteDialog } from './InviteDialog.tsx';
import { LanguageDialog } from './LanguageDialog.tsx';
import { ProfileDialog } from './ProfileDialog.tsx';
import type { ProfileSummary } from '../backgroundProfiles.ts';
import { Avatar } from './Avatar.tsx';
import { currentPushState, disableWebPush, enableWebPush, type PushState } from '../push.ts';
import { formatShortDate, formatTime, formatWeekday } from '../format.ts';
import { useT } from '../i18n.tsx';
import type { MessageKey } from '@vav/i18n';

interface Props {
  client: VavClient;
  conversations: Conversation[];
  activeId?: string;
  onSelect: (id: string) => void;
  contactsById: Record<string, Contact>;
  presence: Record<string, boolean>;
  status: ConnectionStatus;
  profileId: string;
  profiles: ProfileSummary[];
  onSwitchProfile: (id: string) => void;
  onPushEnabled: () => void;
}

/** Catalogue keys, not text: the label is resolved at render so it follows the
    active locale rather than whatever it was at module load. */
const STATUS_LABEL: Record<ConnectionStatus, MessageKey> = {
  online: 'status.online',
  connecting: 'status.connecting',
  offline: 'status.offline',
};

export function Sidebar({
  client,
  conversations,
  activeId,
  onSelect,
  contactsById,
  presence,
  status,
  profileId,
  profiles,
  onSwitchProfile,
  onPushEnabled,
}: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [pushState, setPushState] = useState<PushState>('unsupported');
  const [pushBusy, setPushBusy] = useState(false);
  const [pushDismissed, setPushDismissed] = useState(
    () => localStorage.getItem('vav.pushPromptDismissed') === '1',
  );
  const t = useT();

  useEffect(() => {
    void currentPushState().then(setPushState);
  }, []);

  async function togglePush() {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      const next = pushState === 'on' ? await disableWebPush(client) : await enableWebPush(client);
      setPushState(next);
      // One permission prompt, on the profile the user is looking at, and then
      // every other profile registers the same endpoint so they all ring.
      if (next === 'on') onPushEnabled();
    } finally {
      setPushBusy(false);
    }
  }

  function dismissPushPrompt() {
    localStorage.setItem('vav.pushPromptDismissed', '1');
    setPushDismissed(true);
  }

  const visible = conversations.filter((conversation) =>
    titleOf(conversation, contactsById, t('sidebar.group')).toLowerCase().includes(filter.trim().toLowerCase()),
  );

  return (
    <aside className="sidebar">
      <header className="sidebar__header">
        {/* Your own name and face is where everyone looks for their account, so
            that is what opens it rather than yet another icon in a row of six. */}
        <button
          className="sidebar__me"
          onClick={() => setProfileOpen(true)}
          aria-label={t('profile.title')}
          title={t('profile.title')}
        >
          <Avatar
            name={client.account?.displayName ?? '?'}
            size={38}
            avatar={client.account?.avatar}
            client={client}
          />
          <div className="sidebar__meText">
            <strong>{client.account?.displayName}</strong>
            <span className={`status status--${status}`}>
              <i className="dot" aria-hidden="true" />
              {t(STATUS_LABEL[status])}
            </span>
          </div>
        </button>
        <button
          className="icon"
          onClick={() => setInviteOpen(true)}
          title={t('sidebar.invite')}
          aria-label={t('sidebar.invite')}
        >
          ➕
        </button>
        <button
          className="icon"
          onClick={() => setPhoneOpen(true)}
          title={t('sidebar.findByPhone')}
          aria-label={t('sidebar.findByPhone')}
        >
          📱
        </button>
        <PushToggle state={pushState} busy={pushBusy} onToggle={() => void togglePush()} />
        <button
          className="icon"
          onClick={() => setLanguageOpen(true)}
          title={t('language.title')}
          aria-label={t('language.title')}
        >
          🌐
        </button>
        <button
          className="icon"
          onClick={() => setDialogOpen(true)}
          title={t('sidebar.newChat')}
          aria-label={t('sidebar.newChat')}
        >
          ✏️
        </button>
      </header>

      <div className="sidebar__search">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('sidebar.search')}
          aria-label={t('sidebar.search')}
          dir="auto"
        />
      </div>

      {/*
        Notifications are off and nothing says so.

        A bell in a row of six icons is not an answer to "why did I not get
        told?" -- it carries no state anyone can read and no reason to press it.
        This says what is missing and what pressing it does, once, and goes away
        for good when dismissed.
      */}
      {!pushDismissed && (pushState === 'off' || pushState === 'denied') && (
        <div className={`pushPrompt ${pushState === 'denied' ? 'is-denied' : ''}`}>
          <div className="pushPrompt__body">
            <strong>{t('push.prompt.title')}</strong>
            <span className="muted">
              {pushState === 'denied' ? t('push.prompt.denied') : t('push.prompt.body')}
            </span>
          </div>
          <span className="pushPrompt__actions">
            {pushState === 'off' && (
              <button className="primary" disabled={pushBusy} onClick={() => void togglePush()}>
                {t('push.prompt.enable')}
              </button>
            )}
            <button className="icon" onClick={dismissPushPrompt} aria-label={t('push.prompt.dismiss')}>
              ✕
            </button>
          </span>
        </div>
      )}

      <nav className="conversationList" aria-label={t('sidebar.conversations')}>
        {visible.length === 0 && (
          <p className="empty muted">
            {conversations.length === 0 ? t('sidebar.empty') : t('sidebar.noMatches')}
          </p>
        )}
        {conversations.length === 0 && (
          <button className="primary emptyInvite" onClick={() => setInviteOpen(true)}>
            ➕ {t('sidebar.invite')}
          </button>
        )}
        {visible.map((conversation) => {
          const title = titleOf(conversation, contactsById, t('sidebar.group'));
          const online = conversation.peerId ? presence[conversation.peerId] : undefined;
          return (
            <button
              key={conversation.id}
              className={`conversation ${conversation.id === activeId ? 'is-active' : ''} ${
                conversation.unread > 0 ? 'is-unread' : ''
              } ${conversation.peerGone ? 'is-gone' : ''}`}
              onClick={() => onSelect(conversation.id)}
            >
              <Avatar
                name={title}
                group={conversation.kind === 'group'}
                online={online}
                avatar={conversation.peerId ? contactsById[conversation.peerId]?.avatar : conversation.avatar}
                client={client}
              />

              {/* Three columns, not two rows of mixed content: name over
                  preview on one side, time over count on the other. The time
                  and the badge then share one edge instead of each floating to
                  wherever the text beside it happens to end. */}
              {/* No `dir="auto"` here on purpose.

                  It sets the element's direction from the first strong
                  character, and direction is what alignment follows — so an
                  English name hugged the left edge while a Hebrew one hugged
                  the right, and the list had two ragged edges instead of one.
                  The list is furniture: every row starts on the same side
                  whatever language the name happens to be in. Correct rendering
                  of mixed text is handled by isolation in the stylesheet, which
                  does that job without touching alignment. */}
              <span className="conversation__body">
                <strong>
                  {title}
                  {conversation.peerGone && (
                    <span className="conversation__goneMark" aria-hidden="true"> ⛔</span>
                  )}
                </strong>
                <span className="preview">
                  {conversation.peerGone
                    ? t('thread.recipientGone.title')
                    : conversation.lastPreview || t('sidebar.noMessages')}
                </span>
              </span>

              <span className="conversation__meta">
                <time>{formatWhen(conversation.lastMessageAt)}</time>
                {conversation.unread > 0 && (
                  <span className="badge">{conversation.unread}</span>
                )}
              </span>
            </button>
          );
        })}
      </nav>

      {inviteOpen && <InviteDialog client={client} onClose={() => setInviteOpen(false)} />}

      {profileOpen && (
        <ProfileDialog
          client={client}
          profileId={profileId}
          profiles={profiles}
          onSwitchProfile={onSwitchProfile}
          onClose={() => setProfileOpen(false)}
        />
      )}

      {languageOpen && <LanguageDialog onClose={() => setLanguageOpen(false)} />}

      {phoneOpen && (
        <PhoneDialog
          client={client}
          onClose={() => setPhoneOpen(false)}
          onOpened={(id) => {
            setPhoneOpen(false);
            onSelect(id);
          }}
        />
      )}

      {dialogOpen && (
        <NewChatDialog
          client={client}
          onClose={() => setDialogOpen(false)}
          onOpened={(id) => {
            setDialogOpen(false);
            onSelect(id);
          }}
        />
      )}
    </aside>
  );
}

/**
 * Notification opt-in.
 *
 * Hidden entirely where push cannot work — over plain HTTP on a LAN, for
 * instance — rather than offering a button that silently does nothing.
 */
function PushToggle({
  state,
  busy,
  onToggle,
}: {
  state: PushState;
  busy: boolean;
  onToggle: () => void;
}) {
  const t = useT();
  if (state === 'unsupported' || state === 'insecure') return null;

  const label =
    state === 'on'
      ? t('sidebar.notifications.on')
      : state === 'denied'
        ? t('sidebar.notifications.denied')
        : t('sidebar.notifications.enable');

  return (
    <button
      className="icon"
      onClick={onToggle}
      title={label}
      aria-label={label}
      disabled={busy || state === 'denied'}
      style={{ opacity: state === 'on' ? 1 : 0.45 }}
    >
      {state === 'on' ? '🔔' : '🔕'}
    </button>
  );
}

/**
 * The label goes in as an argument rather than being looked up here: this runs
 * outside React, so it has no access to the active locale, and hardcoding a
 * word would reintroduce exactly the problem the catalogue exists to remove.
 */
export function titleOf(
  conversation: Conversation,
  contacts: Record<string, Contact>,
  fallbackGroupName: string,
): string {
  /*
   * A person's name comes from the live contact, never from the conversation.
   *
   * Both sides used to freeze the peer's display name into `title` at the
   * moment the conversation was created, and this function preferred it — so a
   * rename arrived, updated the contact, and the screen went on showing the
   * name from the day you met. The stored title survives only as a fallback
   * for a contact record that is gone. A group's title is its own thing and
   * stays.
   */
  if (conversation.peerId) {
    return contacts[conversation.peerId]?.displayName ?? conversation.title ?? conversation.peerId;
  }
  return conversation.title ?? fallbackGroupName;
}

/** Relative for today, weekday within the week, otherwise a short date. */
function formatWhen(ts: number): string {
  if (!ts) return '';
  const date = new Date(ts);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) return formatTime(ts);

  const days = Math.floor((now.getTime() - ts) / 86_400_000);
  if (days < 7) return formatWeekday(ts);
  return formatShortDate(ts);
}
